import React, { Children } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { CardContent, Typography, Grid, Box, Tooltip } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { formatTitle } from "./ParcelsHandled";
import Spinner from "../../../components/Spinner/Spinner";
import { useParcels } from "../../../services/clear-customs/overall";
import { formatNumberShort } from "../../../components/formatter";

export function DScanSuccess({ today }) {
  const { formatMessage } = useIntl();
  const { dScan, isLoading } = useParcels();


  const getOptions = (rate) => ({
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 270
    },

    title: {
      text: `${(rate?.dScanSuccessRate || 0)?.toFixed(2)}%`,
      verticalAlign: "middle",
      floating: true,
      style: {
        fontSize: "16px",
        fontWeight: "bold",
        color: "#fff"
      }
    },

    plotOptions: {
      pie: {
        dataLabels: { enabled: false },
        showInLegend: true,
        allowPointSelect: true,
        borderWidth: 0,

        point: {
          events: {
            legendItemClick: function() {
              const point = this;
              setTimeout(() => {
                const chart = point.series.chart;
                const visiblePoints = point.series.points.filter(p => p.visible);
                if (visiblePoints.length > 0) {
                  const active = visiblePoints[0];
                  const y = Number(active?.y);
                  if (!Number.isFinite(y)) {
                    chart.setTitle({ text: `0.00%` });
                    return;
                  }
                  chart.setTitle({ text: `${y.toFixed(2)}%` });
                }
              }, 0);
              return true;
            }
          }
        }
      }
    },
    legend: { enabled: false },
    tooltip: { enabled: false },
    credits: { enabled: false },
    series: [
      {
        name: "D-scan",
        colorByPoint: true,
        data: [
          {
            name: formatMessage({ id: "dScanned" }),
            y: rate?.dScanSuccessRate,
            color: "var(--success-success-500)"
          },
          {
            name: formatMessage({ id: "notDScanned" }),
            y: 100 - rate?.dScanSuccessRate,
            color: "var(--error-error-70)"
          }
        ]
      }
    ]
  });


  return (
    <Box sx={{ borderRadius: "8px", overflow: "hidden", boxShadow: "0 2px 4px -2px var(--10), 0 4px 6px -1px var(--10)", border: "solid 1px var(--new-border)", backgroundColor: "var(--app-body)" }}>
      {isLoading &&
        <Grid container spacing={2}>
          {Children.toArray(
            [...Array(4).keys()].map((_, index) => {
              return (
                <Grid key={index} item xs={12} sm={6} md={3} lg={3}>
                  <Box sx={{ textAlign: "center" }}>
                    <Spinner size={"36px"} />
                  </Box>
                </Grid>
              );
            })
          )}
        </Grid>
      }
      {!isLoading &&
        <>
          {Array.isArray(dScan) && dScan.length > 0 &&
            <Box
              sx={{
                height: "48px",
                flexGrow: 0,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 16px",
                borderBottom: "solid 1px var(--new-border)"
              }}
            >
              <Typography
                data-testid={`dscan_success_heading`}
                color={"var(--text-primary)"} variant="subtitle1" fontWeight={600}
                fontSize={"20px"}
                sx={{
                  height: "28px",
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: "8px",
                  padding: 0
                }}
              >
                <FormattedMessage id="DScanSuccess" />
              </Typography>
            </Box>
          }

          <Grid container spacing={3}>
            {Array.isArray(dScan) && dScan?.map((item, idx) => {
              return (
                <Grid item xs={12} sm={6} md={3} lg={3} key={idx}>
                  <Box sx={{ textAlign: "center" }}>
                    <CardContent>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={getOptions(item)}
                      />
                      <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "4px", mt: "-15px", mb: "14px" }}>
                        {[
                          { color: "var(--success-success-500)", label: formatMessage({ id: "dScanned" }) },
                          { color: "var(--error-error-70)", label: formatMessage({ id: "notDScanned" }) }
                        ].map(({ color, label }) => (
                          <Box key={label} sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "4px" }}>
                            <Box sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: "Inter", fontSize: "10px", fontWeight: 600, color: "var(--font-medium-default)", lineHeight: 1 }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Tooltip title={formatTitle(item)}>
                        <Box sx={{ fontFamily: "Inter", fontSize: "16px", fontWeight: 600, lineHeight: 1.5, textAlign: "center", color: "var(--font-strong-default)" }}>
                          <Tooltip
                            placement="right"
                            title={item?.dScannedParcelsCount?.toLocaleString?.("de-DE") || 0}>
                            <Box component="span" sx={{ fontSize: "20px", lineHeight: 1.4 }}>
                              {formatNumberShort(item?.dScannedParcelsCount) || 0} <FormattedMessage id="parcels" />
                            </Box>
                          </Tooltip>
                          <Box><FormattedMessage id="receivedDScan" /></Box>
                        </Box>
                      </Tooltip>
                    </CardContent>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      }
    </Box>
  );
};





